import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Search, MessageCircle, MoreVertical, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, formatWIB } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { StatusChangeModal } from "@/components/StatusChangeModal";
import {
  PIPELINE_STATUSES, PIPELINE_STATUS_LABELS, PIPELINE_STATUS_CLS, waLink,
} from "@/lib/prospectMeta";

const inputCls = "px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";

function SummaryCard({ label, value, cls, testid }) {
  return (
    <div className={`rounded-xl border p-3 ${cls}`} data-testid={testid}>
      <p className="text-xs font-medium opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-0.5">{value}</p>
    </div>
  );
}

export default function Pipeline() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_aktif: 0, prospek_baru: 0, sedang_diproses: 0, berhasil: 0, tidak_berhasil: 0 });
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState({ q: "", prospect_type: "", program: "", pic: "", status: searchParams.get("status") || "" });
  const [statusFor, setStatusFor] = useState(null); // {prospect, targetStatus}
  const [menuFor, setMenuFor] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prospects/pipeline", { params: filters });
      setItems(data.items); setSummary(data.summary);
    } catch { toast.error("Gagal memuat pipeline."); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/prospects/meta").then(({ data }) => setMembers(data.members)).catch(() => {}); }, []);

  const setF = (k) => (e) => setFilters((p) => ({ ...p, [k]: e.target.value }));

  const quickChange = async (prospect, target) => {
    if (target === "berhasil" || target === "tidak_berhasil") { setStatusFor({ prospect, targetStatus: target }); return; }
    try { await api.patch(`/prospects/${prospect.id}/status`, { status_baru: target }); toast.success("Status diperbarui."); load(); }
    catch { toast.error("Gagal mengubah status."); }
  };

  const onDrop = (target) => (e) => {
    e.preventDefault();
    const id = dragId; setDragId(null);
    if (!id) return;
    const p = items.find((x) => x.id === id);
    if (!p || p.status === target) return;
    quickChange(p, target);
  };

  const colItems = (s) => items.filter((p) => p.status === s || (s === "prospek_baru" && !p.status));

  return (
    <AppLayout>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Pipeline</h1>
        <p className="text-sm text-slate-500">Pantau perkembangan prospek cabang Anda</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="pipeline-summary">
        <SummaryCard testid="summary-total" label="Prospek Aktif" value={summary.total_aktif} cls="bg-white border-slate-200 text-slate-800" />
        <SummaryCard testid="summary-baru" label="Prospek Baru" value={summary.prospek_baru} cls="bg-slate-50 border-slate-200 text-slate-800" />
        <SummaryCard testid="summary-proses" label="Sedang Diproses" value={summary.sedang_diproses} cls="bg-blue-50 border-blue-200 text-blue-800" />
        <SummaryCard testid="summary-berhasil" label="Berhasil / Deal" value={summary.berhasil} cls="bg-green-50 border-green-200 text-green-800" />
        <SummaryCard testid="summary-lost" label="Tidak Berhasil" value={summary.tidak_berhasil} cls="bg-red-50 border-red-200 text-red-800" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="relative lg:col-span-1 sm:col-span-2">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input className={`${inputCls} w-full pl-9`} placeholder="Cari nama, lembaga, WA…" value={filters.q} onChange={setF("q")} data-testid="pipeline-search" />
        </div>
        <select className={inputCls} value={filters.prospect_type} onChange={setF("prospect_type")} data-testid="pipeline-filter-type"><option value="">Semua Jenis</option><option value="individu">Individu</option><option value="lembaga">Lembaga</option></select>
        <input className={inputCls} placeholder="Program…" value={filters.program} onChange={setF("program")} data-testid="pipeline-filter-program" />
        <select className={inputCls} value={filters.pic} onChange={setF("pic")} data-testid="pipeline-filter-pic"><option value="">Semua PIC</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
        <select className={inputCls} value={filters.status} onChange={setF("status")} data-testid="pipeline-filter-status"><option value="">Semua Status</option>{PIPELINE_STATUSES.map((s) => <option key={s} value={s}>{PIPELINE_STATUS_LABELS[s]}</option>)}</select>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
          {PIPELINE_STATUSES.map((s) => {
            const list = colItems(s);
            return (
              <div key={s} className="flex-shrink-0 w-72" data-testid={`pipeline-col-${s}`}
                onDragOver={(e) => e.preventDefault()} onDrop={onDrop(s)}>
                <div className={`rounded-t-lg px-3 py-2 border text-xs font-semibold flex items-center justify-between ${PIPELINE_STATUS_CLS[s]}`}>
                  <span>{PIPELINE_STATUS_LABELS[s]}</span><span className="opacity-70">{list.length}</span>
                </div>
                <div className="bg-slate-100/60 border border-t-0 border-slate-200 rounded-b-lg p-2 space-y-2 min-h-[120px]">
                  {list.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-6">Kosong</p>
                  ) : list.map((p) => {
                    const name = p.institution_name || p.full_name;
                    return (
                      <div key={p.id} draggable onDragStart={() => setDragId(p.id)} onDragEnd={() => setDragId(null)}
                        className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm cursor-grab active:cursor-grabbing relative" data-testid={`pipeline-card-${p.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <button onClick={() => navigate(`/prospek/${p.id}`)} className="text-sm font-semibold text-slate-800 text-left hover:text-[#0f2847]" data-testid={`card-name-${p.id}`}>{name}</button>
                          <div className="relative">
                            <button onClick={() => setMenuFor(menuFor === p.id ? null : p.id)} className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid={`card-menu-${p.id}`}><MoreVertical className="h-4 w-4" /></button>
                            {menuFor === p.id && (
                              <div className="absolute right-0 top-7 z-10 w-48 bg-white border border-slate-200 rounded-lg shadow-lg py-1" data-testid={`card-menu-list-${p.id}`}>
                                <p className="px-3 py-1 text-[10px] uppercase text-slate-400">Ubah Status ke</p>
                                {PIPELINE_STATUSES.filter((x) => x !== p.status).map((x) => (
                                  <button key={x} onClick={() => { setMenuFor(null); quickChange(p, x); }} className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50" data-testid={`card-set-${x}-${p.id}`}>{PIPELINE_STATUS_LABELS[x]}</button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">{p.prospect_type === "lembaga" ? "Lembaga/Corporate" : "Individu"}{p.program_interest ? ` · ${p.program_interest}` : ""}</p>
                        {p.converted && <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] border bg-green-50 text-green-700 border-green-200" data-testid={`card-converted-${p.id}`}>Sudah Dikonversi</span>}
                        {p.phone_normalized && (
                          <a href={waLink(p.phone_normalized)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-green-600 mt-1.5 hover:underline" data-testid={`card-wa-${p.id}`}><MessageCircle className="h-3 w-3" /> {p.phone_normalized}</a>
                        )}
                        {p.assigned_to_name && <p className="text-[11px] text-slate-400 mt-1">PIC: {p.assigned_to_name}</p>}
                        {p.next_follow_up_at && <p className="text-[11px] text-[#f97316] mt-1 inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {formatWIB(p.next_follow_up_at)}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {statusFor && (
        <StatusChangeModal prospect={statusFor.prospect} targetStatus={statusFor.targetStatus}
          onClose={() => setStatusFor(null)} onSaved={() => { setStatusFor(null); load(); }} />
      )}
    </AppLayout>
  );
}
